/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import type Event from '@linc-technologies/linced-common/models/events/event';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// TS-STANDIN(finance/transaction): the transaction model is in linced-app.
// These are the fields that the line item reads.
interface LineItemTransaction extends Model {
  isCancelled: boolean;
  type: number;
  amount: number;
  displayAmount: number;
}

// Proto: finance.LineItem.
export default class LineItem extends Model {
  declare [Type]: 'finance/line-item';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @belongsTo('finance/account', { async: true, inverse: null })
  // TS-STANDIN(finance/account): the account model is in linced-app.
  declare accountId: AsyncBelongsTo<Model>;

  @alias('accountId')
  declare account: AsyncBelongsTo<Model>;

  @alias('account.id')
  declare account_id: string | null | undefined;

  @attr('string', { defaultValue: '' })
  declare name: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare reference: string;

  @attr('string')
  declare date: string | undefined;

  @attr('number', { defaultValue: 0 })
  declare amount: number;

  @attr('number', { defaultValue: 0 })
  declare displayAmount: number;

  @computed('receipts', 'otherCredits')
  get credit() {
    return this.receipts + this.otherCredits;
  }

  @attr('number', { defaultValue: 0 })
  declare taxType: number;

  @attr('number', { defaultValue: 0 })
  declare taxPercentage: number;

  @computed('displayAmount', 'taxPercentage')
  get taxAmount() {
    const rawRate = this.taxPercentage;
    if (rawRate > 0) {
      // Tax percentage is stored as an integer representing tenths of a percent
      const taxRate = rawRate / 100 / 100;
      const exclusive = this.displayAmount / (1 + taxRate);
      return this.displayAmount - exclusive;
    }
    return 0;
  }

  // isTaxable is an ephemeral flag which indicates this line-item should have tax calculated
  // This property is not stored or retrieved from the backend
  // But it used to determine the tax calculation for the line item on the backend
  @attr('boolean', { defaultValue: false })
  declare isTaxable: boolean;

  @attr('number', { defaultValue: 0 })
  declare balance: number;

  @attr('boolean', { defaultValue: false })
  declare isDonation: boolean;

  @attr('boolean', { defaultValue: false })
  declare isArchived: boolean;

  @computed('account_id')
  get isCommunity() {
    return isEmpty(this.account_id);
  }

  @attr('string', { defaultValue: '' })
  declare communityPayerName: string;

  @hasMany('finance/transaction', { async: true, inverse: 'lineItemId' })
  declare transactions: AsyncHasMany<LineItemTransaction>;

  @computed('transactions.content.@each.isCancelled')
  get hasCancelledTransactions() {
    const transactions = resolved(this.transactions);
    if (isEmpty(transactions)) {
      return false;
    }
    return transactions.some((transaction) => transaction.isCancelled);
  }

  @computed('transactions.content.@each.{type,amount,isCancelled}')
  get receipts() {
    const transactions = resolved(this.transactions);
    if (isEmpty(transactions)) {
      return 0;
    }
    return transactions.reduce((total, txn) => {
      if (txn.isCancelled) {
        return total;
      }
      if (
        (txn.type === TransactionType.TRANSFER && txn.amount < 0) ||
        (
          [TransactionType.PAYMENT, TransactionType.APPORTIONMENT] as number[]
        ).includes(txn.type)
      ) {
        return total - txn.amount;
      }
      return total;
    }, 0);
  }

  @computed('transactions.content.@each.{type,amount,isCancelled}')
  get otherCredits() {
    const transactions = resolved(this.transactions);
    if (isEmpty(transactions)) {
      return 0;
    }
    return transactions.reduce((total, txn) => {
      if (txn.isCancelled) {
        return total;
      }
      if (
        (txn.type === TransactionType.TRANSFER && txn.amount > 0) ||
        (
          [
            TransactionType.DISCOUNT___EXEMPTION,
            TransactionType.REFUND,
            TransactionType.WRITEOFF,
            TransactionType.VOID,
          ] as number[]
        ).includes(txn.type)
      ) {
        return total - txn.amount;
      }
      return total;
    }, 0);
  }

  @computed('transactions.content.@each.{type,displayAmount,isCancelled}')
  get adjustments() {
    const transactions = resolved(this.transactions);
    if (isEmpty(transactions)) {
      return 0;
    }
    return transactions.reduce((total, txn) => {
      if (txn.isCancelled) {
        return total;
      }
      if (txn.type === TransactionType.ADJUSTMENT) {
        return total - txn.displayAmount;
      }
      return total;
    }, 0);
  }

  @computed('transactions.content.@each.type')
  get adjustmentCount() {
    const transactions = resolved(this.transactions);
    if (isEmpty(transactions)) {
      return 0;
    }
    return transactions.filter((txn) => txn.type === TransactionType.ADJUSTMENT)
      .length;
  }

  @belongsTo('events/event', { async: true, inverse: null })
  declare eventId: AsyncBelongsTo<Event>;

  @alias('eventId')
  declare event: AsyncBelongsTo<Event>;

  @attr('string', { defaultValue: '' })
  declare optionId: string;

  @attr('string', { defaultValue: '' })
  declare optionValue: string;
}
